import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CLIENT_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  vehicleLabel,
  type BlockedNumber,
  type ClientDetail,
  type Vehicle,
} from "@shared/modules/x-atelier";
import {
  CALL_OUTCOME_LABELS,
  CALL_REQUEST_KIND_LABELS,
  REQUEST_SMS_STATUS_LABELS,
  formatCallDuration,
  type Call,
  type CallRequest,
} from "@shared/modules/x-appels";
import { ApiError } from "../../../core/lib/api";
import { formatDateTime } from "../../../core/lib/format";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input, Field } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { BackLink } from "../../../core/components/ui/back-link";
import { useGoBack } from "../../../core/lib/navigation";
import { VehicleCorrectionHint } from "../components/vehicle-identity";
import { AppointmentModal, type AppointmentPreset } from "../components/appointment-modal";
import { ClientStatusBadge } from "../components/booking-badges";
import { ClientFormModal } from "../components/client-form-modal";
import { JobRow } from "../components/job-row";
import { QuickActions } from "../components/quick-actions";
import { VehicleFormModal } from "../components/vehicle-form-modal";
import {
  blockNumber,
  checkBlockedNumber,
  deleteClient,
  deleteVehicle,
  getClient,
  listClientCalls,
  listClientRequests,
  markRequestHandled,
  unblockNumber,
} from "../lib/api";

type Failure = "not_found" | "error" | null;

/** Fiche client : coordonnées, véhicules (modal), historique des rendez-vous, nouveau RDV pré-rempli. */
export function ClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // « Retour » revient à la page d’origine (liste filtrée, rendez-vous…), sinon aux clients.
  const goBack = useGoBack("/clients");
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");

  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [editing, setEditing] = useState(false);
  const [vehicleModal, setVehicleModal] = useState<{ open: boolean; vehicle?: Vehicle }>({
    open: false,
  });
  const [rdv, setRdv] = useState<{ open: boolean; preset?: AppointmentPreset }>({ open: false });
  const [confirmVehicle, setConfirmVehicle] = useState<Vehicle | null>(null);
  const [confirmClient, setConfirmClient] = useState(false);
  const [busy, setBusy] = useState(false);
  // Liste noire : le numéro de la fiche est-il bloqué ? Et les demandes prises par Léo.
  const [blocked, setBlocked] = useState<BlockedNumber | null>(null);
  const [blocking, setBlocking] = useState<{ open: boolean; reason: string }>({
    open: false,
    reason: "",
  });
  const [requests, setRequests] = useState<CallRequest[]>([]);
  const [calls, setCalls] = useState<Call[]>([]);

  const load = useCallback(() => {
    if (!id) return;
    listClientRequests(id)
      .then(setRequests)
      .catch(() => setRequests([]));
    listClientCalls(id)
      .then(setCalls)
      .catch(() => setCalls([]));
    getClient(id)
      .then((d) => {
        setDetail(d);
        setFailure(null);
        if (d.client.phone)
          checkBlockedNumber(d.client.phone)
            .then(setBlocked)
            .catch(() => setBlocked(null));
        else setBlocked(null);
      })
      .catch((err: unknown) => {
        setDetail(null);
        setFailure(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      });
  }, [id]);

  useEffect(load, [load]);

  const removeVehicle = async () => {
    if (!confirmVehicle || busy) return;
    setBusy(true);
    try {
      await deleteVehicle(confirmVehicle.id);
      push("success", "Véhicule supprimé");
      setConfirmVehicle(null);
      load();
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const block = async () => {
    if (!detail?.client.phone || busy) return;
    setBusy(true);
    try {
      setBlocked(
        await blockNumber({ phone: detail.client.phone, reason: blocking.reason.trim() || null }),
      );
      setBlocking({ open: false, reason: "" });
      push(
        "success",
        "Numéro bloqué",
        "Léo ne prend plus de rendez-vous à ce numéro et le site refuse ses réservations.",
      );
    } catch {
      push("danger", "Blocage impossible");
    } finally {
      setBusy(false);
    }
  };
  const unblock = async () => {
    if (!blocked || busy) return;
    setBusy(true);
    try {
      await unblockNumber(blocked.id);
      setBlocked(null);
      push("success", "Numéro débloqué");
    } catch {
      push("danger", "Déblocage impossible");
    } finally {
      setBusy(false);
    }
  };
  const handleRequest = async (r: CallRequest) => {
    try {
      const next = await markRequestHandled(r.id);
      setRequests((list) => list.map((x) => (x.id === next.id ? next : x)));
      push("success", "Demande traitée");
    } catch {
      push("danger", "Mise à jour impossible");
    }
  };

  const removeClient = async () => {
    if (!id || busy) return;
    setBusy(true);
    try {
      await deleteClient(id);
      push("success", "Client supprimé");
      goBack();
    } catch {
      push("danger", "Suppression impossible");
      setBusy(false);
    }
  };

  if (failure) {
    return (
      <div className="page">
        <p className="atelier-backlink">
          <BackLink fallback="/clients" />
        </p>
        <p className="empty">
          {failure === "not_found"
            ? "Client introuvable — il a peut-être été supprimé."
            : "Fiche indisponible — réessayez dans un instant."}
        </p>
      </div>
    );
  }

  if (!detail || !id) return <p className="empty">Chargement…</p>;

  const c = detail.client;
  const address = [c.address, c.city, c.postalCode].filter(Boolean).join(", ");
  const openRdv = (vehicleId?: string) =>
    setRdv({
      open: true,
      preset: { client: { id: c.id, name: c.name, phone: c.phone }, vehicleId },
    });

  return (
    <div className="page client">
      <p className="atelier-backlink">
        <BackLink fallback="/clients" />
        {isAdmin && c.id.startsWith("urable:client:") ? (
          <>
            {" "}
            ·{" "}
            <Link
              to={`/donnees-urable?dataset=clients&q=${encodeURIComponent(c.id.slice("urable:client:".length))}`}
            >
              Données Urable et consentements
            </Link>
          </>
        ) : null}
      </p>

      <header className="client__head">
        <div className="client__titles">
          <h1 className="client__name">{c.name}</h1>
          <p className="client__sub">
            <Badge variant={c.type === "particulier" ? "neutral" : "primary"}>
              {CLIENT_TYPE_LABELS[c.type]}
            </Badge>
            <ClientStatusBadge status={c.status} confirmedAt={c.confirmedAt} />
            {blocked ? <Badge variant="danger">Numéro bloqué</Badge> : null}
            {c.phone ? <span>{c.phone}</span> : null}
            {c.email ? <span>{c.email}</span> : null}
          </p>
          <QuickActions phone={c.phone} email={c.email} address={address || null} />
        </div>
        <div className="client__actions">
          <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => openRdv()}>
            Nouveau rendez-vous
          </Button>
          <Button
            variant="ghost"
            icon={<Icon name="edit" size={16} />}
            onClick={() => setEditing(true)}
          >
            Modifier
          </Button>
          {isAdmin && c.phone ? (
            blocked ? (
              <Button variant="ghost" disabled={busy} onClick={() => void unblock()}>
                Débloquer le numéro
              </Button>
            ) : (
              <Button
                variant="ghost-danger"
                icon={<Icon name="alert-circle" size={16} />}
                onClick={() => setBlocking({ open: true, reason: "" })}
              >
                Bloquer le numéro
              </Button>
            )
          ) : null}
          {isAdmin ? (
            <Button variant="ghost-danger" onClick={() => setConfirmClient(true)}>
              Supprimer
            </Button>
          ) : null}
        </div>
      </header>

      {blocked ? (
        <p className="client__blocked" role="status">
          <Icon name="alert-circle" size={18} />
          <span>
            <strong>Numéro bloqué</strong> depuis le{" "}
            {formatDateTime(blocked.createdAt, { dateStyle: "medium" })}
            {blocked.createdByName ? ` par ${blocked.createdByName}` : ""}
            {blocked.reason ? ` — ${blocked.reason}` : ""}. Léo ne prend aucun rendez-vous à ce
            numéro, la réservation en ligne est refusée et chaque appel est inscrit au journal.
          </span>
        </p>
      ) : null}

      {requests.filter((r) => r.status === "a_traiter").length ? (
        <div className="client__blocked" role="status" style={{ borderColor: "transparent" }}>
          <Icon name="phone" size={18} />
          <span>
            {requests
              .filter((r) => r.status === "a_traiter")
              .map((r) => (
                <span key={r.id} style={{ display: "block" }}>
                  <strong>Léo : {CALL_REQUEST_KIND_LABELS[r.kind].toLowerCase()}</strong> le{" "}
                  {formatDateTime(r.createdAt, { dateStyle: "medium", timeStyle: "short" })}
                  {r.appointmentLabel ? ` · RDV du ${r.appointmentLabel}` : ""}
                  {r.reason ? ` · ${r.reason}` : ""} · {REQUEST_SMS_STATUS_LABELS[r.smsStatus]}{" "}
                  <Button variant="ghost" size="sm" onClick={() => void handleRequest(r)}>
                    Marquer traitée
                  </Button>
                </span>
              ))}
          </span>
        </div>
      ) : null}

      <div className="client__grid">
        <section className="card client__block">
          <h2 className="client__block-title">Coordonnées</h2>
          <dl className="kv">
            <dt>Téléphone</dt>
            <dd>{c.phone ?? <span className="client__empty">—</span>}</dd>
            <dt>Courriel</dt>
            <dd>{c.email ?? <span className="client__empty">—</span>}</dd>
            <dt>Adresse</dt>
            <dd>
              {[c.address, c.city, c.postalCode].filter(Boolean).join(", ") || (
                <span className="client__empty">—</span>
              )}
            </dd>
            <dt>SMS promo</dt>
            <dd>
              {c.smsConsent ? (
                <Badge variant="success">Consent</Badge>
              ) : (
                <Badge variant="neutral">Sans consentement</Badge>
              )}
            </dd>
            <dt>Source</dt>
            <dd>{LEAD_SOURCE_LABELS[c.source]}</dd>
            <dt>Client depuis</dt>
            <dd>{formatDateTime(c.createdAt, { dateStyle: "medium" })}</dd>
          </dl>
          {c.notes ? <p className="client__notes">{c.notes}</p> : null}
        </section>

        <section className="card client__block">
          <div className="client__block-head">
            <h2 className="client__block-title">Véhicules</h2>
            <Button
              variant="ghost"
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() => setVehicleModal({ open: true })}
            >
              Ajouter
            </Button>
          </div>
          {detail.vehicles.length === 0 ? (
            <p className="client__empty-block">Aucun véhicule enregistré.</p>
          ) : (
            <ul className="client__vehicles">
              {detail.vehicles.map((v) => (
                <li key={v.id} className="vehicle-card">
                  <div className="vehicle-card__main">
                    <span className="vehicle-card__label">{vehicleLabel(v)}</span>
                    <span className="vehicle-card__meta">
                      {[
                        v.plate,
                        v.color,
                        v.mileage !== null ? `${v.mileage.toLocaleString("fr-CA")} km` : null,
                        v.vin ? `NIV ${v.vin}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Aucun détail"}
                    </span>
                    {v.notes ? <span className="vehicle-card__notes">{v.notes}</span> : null}
                    <VehicleCorrectionHint vehicle={v} onFixed={() => load()} />
                  </div>
                  <div className="vehicle-card__actions">
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<Icon name="plus" size={14} />}
                      onClick={() => openRdv(v.id)}
                    >
                      Rendez-vous
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setVehicleModal({ open: true, vehicle: v })}
                    >
                      Modifier
                    </Button>
                    {isAdmin ? (
                      <Button variant="ghost-danger" size="sm" onClick={() => setConfirmVehicle(v)}>
                        Supprimer
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="card client__block">
        <h2 className="client__block-title">Historique des rendez-vous</h2>
        {detail.appointments.length === 0 ? (
          <p className="client__empty-block">Aucun rendez-vous pour ce client.</p>
        ) : (
          <div className="job-list" role="list">
            {detail.appointments.map((a) => (
              <JobRow key={a.id} appointment={a} showClient={false} />
            ))}
          </div>
        )}
      </section>

      {calls.length ? (
        <section className="card client__block">
          <h2 className="client__block-title">Appels</h2>
          <ul className="client__calls">
            {calls.map((a) => (
              <li key={a.id}>
                <Link to={`/appels/${a.id}`} className="client__call">
                  <span className="client__call-meta">
                    {formatDateTime(a.startedAt, { dateStyle: "medium", timeStyle: "short" })} ·{" "}
                    {CALL_OUTCOME_LABELS[a.outcome]}
                    {a.durationSec ? ` · ${formatCallDuration(a.durationSec)}` : ""}
                    {a.intent ? ` · ${a.intent}` : ""}
                  </span>
                  {a.summary ? <span className="client__call-summary">{a.summary}</span> : null}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ClientFormModal
        open={editing}
        client={c}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          load();
        }}
      />

      <VehicleFormModal
        open={vehicleModal.open}
        clientId={id}
        vehicle={vehicleModal.vehicle}
        onClose={() => setVehicleModal({ open: false })}
        onSaved={() => {
          setVehicleModal({ open: false });
          load();
        }}
      />

      <AppointmentModal
        open={rdv.open}
        preset={rdv.preset}
        onClose={() => setRdv({ open: false })}
        onSaved={(a) => {
          setRdv({ open: false });
          navigate(`/travaux/${a.id}`);
        }}
      />

      <Modal
        open={confirmVehicle !== null}
        title="Supprimer ce véhicule ?"
        onClose={() => setConfirmVehicle(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmVehicle(null)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void removeVehicle()}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          {confirmVehicle ? vehicleLabel(confirmVehicle) : ""} sera retiré de la fiche. Les
          rendez-vous passés gardent leur titre.
        </p>
      </Modal>

      <Modal
        open={blocking.open}
        title="Bloquer ce numéro"
        onClose={() => setBlocking({ open: false, reason: "" })}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBlocking({ open: false, reason: "" })}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void block()}>
              Bloquer {c.phone}
            </Button>
          </>
        }
      >
        <p>
          Léo ne prendra plus aucun rendez-vous ni message à ce numéro, la réservation en ligne sera
          refusée et chaque appel sera inscrit au journal. La fiche et l’historique restent.
        </p>
        <Field label="Raison (interne, facultatif)">
          <Input
            value={blocking.reason}
            maxLength={300}
            placeholder="ex : rendez-vous manqués à répétition"
            onChange={(e) => setBlocking((b) => ({ ...b, reason: e.target.value }))}
          />
        </Field>
      </Modal>

      <Modal
        open={confirmClient}
        title="Supprimer ce client ?"
        onClose={() => setConfirmClient(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmClient(false)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void removeClient()}>
              Supprimer définitivement
            </Button>
          </>
        }
      >
        <p>
          « {c.name} », ses véhicules et son historique de rendez-vous seront supprimés. Cette
          action est définitive.
        </p>
      </Modal>
    </div>
  );
}
